import React, { useState, useEffect } from 'react';
import './DssManager.css';
import { et0Display } from '../../utils/waterBalanceView';
import { waterBalanceService } from '../../services/waterBalanceService';

const API = process.env.REACT_APP_API_BASE_URL || 'http://169.58.21.41/api';

// Parameter groups for the DSS rule builder
const SENSOR_GROUPS = [
    {
        groupTR: 'ET0 × Kc — Bitki Su İhtiyacı',
        groupEN: 'ET0 × Kc — Crop Water Demand',
        sensors: [
            { value: 'etc',  icon: '🌿', labelTR: 'ET0×Kc (mm/gün)',   labelEN: 'ET0×Kc (mm/day)',   unit: 'mm/d' },
            { value: 'et0',  icon: '☀️', labelTR: 'ET0 Referans',       labelEN: 'ET0 Reference',     unit: 'mm/d' },
            { value: 'kc',   icon: '🌾', labelTR: 'Kc Bitki Katsayısı', labelEN: 'Kc Crop Coeff.',    unit: ''     },
        ],
    },
    {
        groupTR: 'Uydu / Drone Multispektral',
        groupEN: 'Satellite / Drone Multispectral',
        sensors: [
            { value: 'ndvi', icon: '🛸', labelTR: 'NDVI (Bitki Örtüsü)', labelEN: 'NDVI (Vegetation)', unit: ''  },
            { value: 'ndmi', icon: '💧', labelTR: 'NDMI (Su Stresi)',     labelEN: 'NDMI (Moisture)',   unit: ''  },
            { value: 'ndre', icon: '🌿', labelTR: 'NDRE (Klorofil)',      labelEN: 'NDRE (Chlorophyll)',unit: ''  },
        ],
    },
    {
        groupTR: 'Toprak & Su Kalitesi',
        groupEN: 'Soil & Water Quality',
        sensors: [
            { value: 'soilMoisture', icon: '💦', labelTR: 'Toprak Nemi',       labelEN: 'Soil Moisture',    unit: '%'      },
            { value: 'ph',           icon: '⚗️', labelTR: 'pH',                labelEN: 'Soil pH',          unit: ''       },
            { value: 'ec',           icon: '⚡', labelTR: 'EC (Tuzluluk)',      labelEN: 'EC (Salinity)',    unit: 'mS/cm'  },
            { value: 'nitrogen',     icon: '🧪', labelTR: 'Azot (N)',           labelEN: 'Nitrogen (N)',     unit: 'ppm'    },
            { value: 'phosphorus',   icon: '🧪', labelTR: 'Fosfor (P)',         labelEN: 'Phosphorus (P)',   unit: 'ppm'    },
            { value: 'potassium',    icon: '🧪', labelTR: 'Potasyum (K)',       labelEN: 'Potassium (K)',    unit: 'ppm'    },
            { value: 'dissolvedO2',  icon: '🫧', labelTR: 'Çözünmüş O₂',       labelEN: 'Dissolved O₂',    unit: 'mg/L'   },
            { value: 'turbidity',    icon: '🌫', labelTR: 'Bulanıklık',         labelEN: 'Turbidity',        unit: 'NTU'    },
        ],
    },
    {
        groupTR: 'Sensör (Eski)',
        groupEN: 'IoT Sensor (Legacy)',
        sensors: [
            { value: 'temperature', icon: '🌡', labelTR: 'Hava Sıcaklığı',   labelEN: 'Air Temperature',  unit: '°C'  },
            { value: 'humidity',    icon: '💦', labelTR: 'Hava Nemi',         labelEN: 'Air Humidity',     unit: '%'   },
            { value: 'soilTemp',    icon: '🌱', labelTR: 'Toprak Sıcaklığı', labelEN: 'Soil Temperature', unit: '°C'  },
            { value: 'rainfall',    icon: '🌧', labelTR: 'Yağış',             labelEN: 'Rainfall',         unit: 'mm'  },
            { value: 'windSpeed',   icon: '💨', labelTR: 'Rüzgar Hızı',       labelEN: 'Wind Speed',       unit: 'm/s' },
        ],
    },
];

// Flat list for lookup (all sensors)
const SENSORS = SENSOR_GROUPS.flatMap(g => g.sensors);

const OPS = [
    { value: '<',  labelTR: '< Küçüktür',    labelEN: '< Less than'      },
    { value: '>',  labelTR: '> Büyüktür',    labelEN: '> Greater than'   },
    { value: '<=', labelTR: '≤ Küçük eşit',  labelEN: '≤ Less or equal'  },
    { value: '>=', labelTR: '≥ Büyük eşit',  labelEN: '≥ Greater or equal'},
    { value: '==', labelTR: '= Eşittir',     labelEN: '= Equals'         },
];

function newCond() {
    return { id: Date.now() + Math.random(), sensor: 'etc', op: '>', threshold: 5, reason: '', join: null };
}

export default function DssManager({ fieldId, fieldName, cropType, fieldKc, token, language }) {
    const isEN = language === 'en';
    const sLabel = (s) => isEN ? s.labelEN : s.labelTR;
    const oLabel = (o) => isEN ? o.labelEN : o.labelTR;

    const [dss, setDss] = useState(null);
    const [tab, setTab] = useState('builder');
    const [conditions, setConditions] = useState([newCond()]);
    const [dssName, setDssName] = useState('');
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);
    const [msg, setMsg] = useState(null);

    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    const toast = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3000); };

    useEffect(() => {
        fetch(`${API}/irrigation/${fieldId}/dss`, { headers })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                setDss(data);
                if (!data) return;
                setDssName(data.name || '');
                setTab(data.type === 'python' ? 'python' : 'builder');
                if (data.rulesJson?.conditions?.length) {
                    const conds = data.rulesJson.conditions.map((c, i) => ({
                        ...c,
                        id: Date.now() + i,
                        // join: operator before this condition (null for first)
                        join: i === 0 ? null : (c.join || 'AND'),
                    }));
                    setConditions(conds);
                }
            });
    }, [fieldId]);

    const update = (id, field, value) =>
        setConditions(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));

    const remove = (id) =>
        setConditions(prev => {
            const next = prev.filter(c => c.id !== id);
            // If we removed the first item, clear join on new first
            if (next.length > 0) next[0] = { ...next[0], join: null };
            return next.length ? next : [newCond()];
        });

    const add = () =>
        setConditions(prev => [...prev, { ...newCond(), join: 'AND' }]);

    const handleSave = async () => {
        const rulesJson = {
            conditions: conditions.map(({ id, ...rest }) => ({
                ...rest,
                threshold: Number(rest.threshold),
            })),
        };
        setSaving(true);
        const r = await fetch(`${API}/irrigation/${fieldId}/dss`, {
            method: 'POST', headers,
            body: JSON.stringify({ name: dssName || (fieldName ? `${fieldName} DSS` : 'DSS'), rulesJson }),
        });
        if (r.ok) { setDss(await r.json()); toast('ok', isEN ? 'Saved.' : 'Kaydedildi.'); }
        else toast('err', isEN ? 'Save failed.' : 'Kayıt başarısız.');
        setSaving(false);
    };

    const handleRun = async () => {
        setRunning(true);
        const r = await fetch(`${API}/irrigation/${fieldId}/dss/run`, { method: 'POST', headers });
        if (r.ok) {
            const d = await r.json();
            setDss(prev => ({ ...prev, lastResult: d.result, lastRunAt: new Date().toISOString() }));
            toast('ok', d.result?.needsIrrigation
                ? (isEN ? '💧 Irrigation needed!' : '💧 Sulama gerekli!')
                : (isEN ? '✅ No irrigation needed.' : '✅ Sulama gerekmez.'));
        } else toast('err', isEN ? 'Run failed.' : 'Çalıştırma başarısız.');
        setRunning(false);
    };

    const handleUpload = async () => {
        if (!file) return;
        setSaving(true);
        const form = new FormData();
        form.append('script', file);
        const r = await fetch(`${API}/irrigation/${fieldId}/dss/upload`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
        });
        if (r.ok) { setDss(await r.json()); toast('ok', isEN ? 'Uploaded.' : 'Yüklendi.'); }
        else toast('err', isEN ? 'Upload failed.' : 'Yükleme başarısız.');
        setSaving(false);
    };

    const handleDelete = async () => {
        if (!window.confirm(isEN ? 'Delete DSS?' : 'DSS silinsin mi?')) return;
        await fetch(`${API}/irrigation/${fieldId}/dss`, { method: 'DELETE', headers });
        setDss(null); setConditions([newCond()]); setDssName('');
    };

    const getSensor = (v) => SENSORS.find(s => s.value === v) || SENSORS[0];

    // Build human-readable preview
    const preview = conditions.map((c, i) => {
        const s = getSensor(c.sensor);
        const part = `${s.icon} ${sLabel(s)} ${c.op} ${c.threshold}${s.unit}`;
        if (i === 0) return part;
        return ` ${c.join === 'OR' ? (isEN ? 'OR' : 'VEYA') : (isEN ? 'AND' : 'VE')} ${part}`;
    }).join('');

    // ── ET₀ / ETc banner: the server's rolling-24 h FAO-56, i.e. exactly what the DSS rules see ──
    const [balance, setBalance] = useState(null);
    useEffect(() => {
        let alive = true;
        const load = () => waterBalanceService.fetchOne(fieldId).then(b => alive && setBalance(b)).catch(() => {});
        load();
        const id = setInterval(load, 5 * 60 * 1000);
        return () => { alive = false; clearInterval(id); };
    }, [fieldId]);
    const et0Banner = React.useMemo(() => {
        const d = et0Display(balance, isEN ? 'en' : 'tr');
        if (d.value == null) return null;
        const kc = typeof fieldKc === 'number' ? fieldKc : (balance?.kc ?? null);
        const etc = kc != null ? Math.round(d.value * kc * 100) / 100 : null;
        return { et0: d.value, etc, kc };
    }, [balance, fieldKc, isEN]);

    return (
        <div className="dss-wrap">
            {/* ET₀ / ETc banner */}
            {et0Banner && (
                <div className="dss-etc-banner">
                    <div className="dss-etc-row">
                        <div className="dss-etc-item">
                            <span className="dss-etc-lbl">ET₀</span>
                            <span className="dss-etc-val">{et0Banner.et0} <small>mm/day</small></span>
                        </div>
                        {et0Banner.etc != null && (
                            <>
                                <span className="dss-etc-x">×</span>
                                <div className="dss-etc-item">
                                    <span className="dss-etc-lbl">Kc</span>
                                    <span className="dss-etc-val">{et0Banner.kc}</span>
                                </div>
                                <span className="dss-etc-x">=</span>
                                <div className="dss-etc-item">
                                    <span className="dss-etc-lbl">ETc</span>
                                    <span className="dss-etc-val dss-etc-highlight">{et0Banner.etc} <small>mm/day</small></span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {/* Last result banner */}
            {dss?.lastResult && (
                <div className={`dss-result ${dss.lastResult.needsIrrigation ? 'dss-result-water' : 'dss-result-ok'}`}>
                    <span>{dss.lastResult.needsIrrigation ? '💧' : '✅'}</span>
                    <span className="dss-result-text">
                        {dss.lastResult.needsIrrigation
                            ? (isEN ? 'Irrigation needed' : 'Sulama gerekli')
                            : (isEN ? 'No irrigation needed' : 'Sulama gerekmez')}
                        {dss.lastResult.reason ? ` — ${dss.lastResult.reason}` : ''}
                    </span>
                    <button className="dss-run-btn" onClick={handleRun} disabled={running}>
                        {running ? '⏳' : '▶'}
                    </button>
                </div>
            )}

            {msg && (
                <div className={`dss-toast ${msg.type === 'ok' ? 'dss-toast-ok' : 'dss-toast-err'}`}>
                    {msg.text}
                </div>
            )}

            {/* Tabs */}
            <div className="dss-tabs">
                {[
                    { key: 'builder', label: isEN ? '🧩 Rule Builder' : '🧩 Kural Oluşturucu' },
                    { key: 'python',  label: isEN ? '🐍 Python Script' : '🐍 Python Script' },
                ].map(t => (
                    <button key={t.key} className={`dss-tab ${tab === t.key ? 'dss-tab-active' : ''}`} onClick={() => setTab(t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* DSS name */}
            <input
                className="dss-name-input"
                value={dssName}
                onChange={e => setDssName(e.target.value)}
                placeholder={isEN ? 'Rule set name...' : 'Kural seti adı...'}
            />

            {tab === 'builder' && (
                <>
                    {/* Single condition box */}
                    <div className="dss-box">
                        {conditions.map((cond, idx) => {
                            const s = getSensor(cond.sensor);
                            return (
                                <div key={cond.id}>
                                    {/* Join operator row (shown between conditions) */}
                                    {idx > 0 && (
                                        <div className="dss-join-row">
                                            <div className="dss-join-line" />
                                            <div className="dss-join-toggle">
                                                <button
                                                    className={`dss-join-btn ${cond.join === 'AND' ? 'active-and' : ''}`}
                                                    onClick={() => update(cond.id, 'join', 'AND')}
                                                >
                                                    {isEN ? 'AND' : 'VE'}
                                                </button>
                                                <button
                                                    className={`dss-join-btn ${cond.join === 'OR' ? 'active-or' : ''}`}
                                                    onClick={() => update(cond.id, 'join', 'OR')}
                                                >
                                                    {isEN ? 'OR' : 'VEYA'}
                                                </button>
                                            </div>
                                            <div className="dss-join-line" />
                                        </div>
                                    )}

                                    {/* Condition card */}
                                    <div className="dss-cond-card">
                                        <div className="dss-cond-num">{idx + 1}</div>

                                        {/* Sensor chips — grouped */}
                                        <div className="dss-cond-section">
                                            <span className="dss-cond-label">
                                                {isEN ? 'Parameter' : 'Parametre'}
                                            </span>
                                            {SENSOR_GROUPS.map(grp => (
                                                <div key={grp.groupEN} className="dss-chip-group">
                                                    <span className="dss-chip-group-label">
                                                        {isEN ? grp.groupEN : grp.groupTR}
                                                    </span>
                                                    <div className="dss-sensor-chips">
                                                        {grp.sensors.map(so => (
                                                            <button
                                                                key={so.value}
                                                                className={`dss-chip ${cond.sensor === so.value ? 'chip-active' : ''}`}
                                                                onClick={() => update(cond.id, 'sensor', so.value)}
                                                            >
                                                                {so.icon} {sLabel(so)}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Op + threshold */}
                                        <div className="dss-cond-section">
                                            <span className="dss-cond-label">
                                                {isEN ? 'Condition' : 'Koşul'}
                                            </span>
                                            <div className="dss-cond-inline">
                                                <span className="dss-badge">{s.icon} {sLabel(s)}</span>
                                                <select
                                                    className="dss-select"
                                                    value={cond.op}
                                                    onChange={e => update(cond.id, 'op', e.target.value)}
                                                >
                                                    {OPS.map(o => (
                                                        <option key={o.value} value={o.value}>{oLabel(o)}</option>
                                                    ))}
                                                </select>
                                                <input
                                                    type="number"
                                                    className="dss-threshold"
                                                    value={cond.threshold}
                                                    onChange={e => update(cond.id, 'threshold', e.target.value)}
                                                />
                                                <span className="dss-unit">{s.unit}</span>
                                            </div>
                                        </div>

                                        {/* Reason */}
                                        <div className="dss-cond-section">
                                            <span className="dss-cond-label">
                                                {isEN ? 'Reason (optional)' : 'Açıklama (opsiyonel)'}
                                            </span>
                                            <input
                                                className="dss-reason"
                                                value={cond.reason}
                                                onChange={e => update(cond.id, 'reason', e.target.value)}
                                                placeholder={isEN ? 'e.g. Soil too dry' : 'örn. Toprak çok kuru'}
                                            />
                                        </div>

                                        {conditions.length > 1 && (
                                            <button className="dss-cond-del" onClick={() => remove(cond.id)} title={isEN ? 'Remove' : 'Kaldır'}>✕</button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Add condition */}
                        <button className="dss-add-cond" onClick={add}>
                            + {isEN ? 'Add Condition' : 'Koşul Ekle'}
                        </button>
                    </div>

                    {/* Preview */}
                    {conditions.length > 0 && (
                        <div className="dss-preview">
                            <span className="dss-preview-lbl">{isEN ? 'Preview:' : 'Önizleme:'}</span>
                            <span className="dss-preview-txt">{preview}</span>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="dss-actions">
                        <button className="dss-btn-save" onClick={handleSave} disabled={saving}>
                            {saving ? '⏳' : '💾'} {isEN ? 'Save' : 'Kaydet'}
                        </button>
                        {dss && (
                            <button className="dss-btn-run" onClick={handleRun} disabled={running}>
                                {running ? '⏳' : '▶'} {isEN ? 'Test' : 'Test Et'}
                            </button>
                        )}
                        {dss && (
                            <button className="dss-btn-del" onClick={handleDelete}>🗑</button>
                        )}
                    </div>
                </>
            )}

            {tab === 'python' && (
                <div className="dss-python">
                    <div className="dss-py-hint">
                        {isEN ? 'Script must print JSON to stdout:' : 'Script stdout\'a JSON çıktısı vermeli:'}
                        <code className="dss-py-code">
                            {`{"needsIrrigation": true, "reason": "...", "confidence": 0.9}`}
                        </code>
                    </div>
                    <label className="dss-py-label">
                        <input type="file" accept=".py" onChange={e => setFile(e.target.files[0])} style={{ display: 'none' }} />
                        <span className="dss-py-btn">
                            📁 {file ? file.name : (isEN ? 'Choose .py file' : '.py dosyası seç')}
                        </span>
                    </label>
                    <button className="dss-btn-save" onClick={handleUpload} disabled={!file || saving}>
                        {saving ? '⏳' : '📤'} {isEN ? 'Upload' : 'Yükle'}
                    </button>
                    {dss?.type === 'python' && (
                        <div className="dss-actions" style={{ marginTop: 6 }}>
                            <button className="dss-btn-run" onClick={handleRun} disabled={running}>
                                {running ? '⏳' : '▶'} {isEN ? 'Run' : 'Çalıştır'}
                            </button>
                            <button className="dss-btn-del" onClick={handleDelete}>🗑</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
